import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  MinusCircle,
  Printer,
  Wallet,
} from "lucide-react";

import {
  Button as ButtonRaw,
  Card,
  CardHeader as CardHeaderRaw,
  Spinner,
  StatusPill as StatusPillRaw,
  colors,
  spacing,
  typography,
} from "../../design-system";

import { backButtonStyle, iconWrapStyle, thLeft, thRight } from "./styles";
import { formatCurrency, formatDate, getStubName, pick } from "./helpers";
import { usePayStubDetail } from "./usePayStubDetail";

import ErrorBlock from "./components/ErrorBlock";
import SummaryStat from "./components/SummaryStat";
import LineRow from "./components/LineRow";
import DeductionRow from "./components/DeductionRow";
import NetPayHighlight from "./components/NetPayHighlight";

const Button = ButtonRaw as any;
const CardHeader = CardHeaderRaw as any;
const StatusPill = StatusPillRaw as any;

export default function PayStubDetail() {
  const { runId, stubId } = useParams<{ runId: string; stubId: string }>();
  const navigate = useNavigate();

  const { data, isLoading: loading, error, refetch } = usePayStubDetail(
    runId,
    stubId
  );

  if (loading) {
    return (
      <div
        style={{
          background: colors.bgPage,
          minHeight: "100%",
          padding: spacing[10] + "px",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            textAlign: "center",
            paddingTop: spacing[12],
          }}
        >
          <Spinner size={20} label="Loading pay stub..." inline />
        </div>
      </div>
    );
  }

  const errMsg = error instanceof Error ? error.message : null;

  if (errMsg || !data) {
    return (
      <div
        style={{
          background: colors.bgPage,
          minHeight: "100%",
          fontFamily: typography.fontFamily,
          padding: spacing[6] + "px " + spacing[8] + "px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ width: "100%" }}>
          <button
            onClick={() => navigate("/payroll/runs/" + runId)}
            style={{ ...backButtonStyle, marginBottom: spacing[6] }}
          >
            <ArrowLeft size={16} /> Back to pay run
          </button>
          <ErrorBlock
            title="Could not load pay stub"
            message={errMsg || "Not found"}
          />
          <Button variant="secondary" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const { run, stub } = data;
  const currency = stub.currency || run?.currency || "CAD";
  const status = String(run?.status || stub.status || "draft").toLowerCase();
  const stubRecord = stub as Record<string, unknown>;

  // Earnings
  const regularHours = pick<number>(stubRecord, "regular_hours", "hours_regular");
  const regularPay = pick<number>(stubRecord, "regular_pay", "pay_regular");
  const overtimeHours = pick<number>(stubRecord, "overtime_hours", "hours_overtime", "ot_hours");
  const overtimePay = pick<number>(stubRecord, "overtime_pay", "ot_pay");
  const vacationHours = pick<number>(stubRecord, "vacation_hours", "hours_vacation");
  const vacationPay = pick<number>(stubRecord, "vacation_pay", "pay_vacation");
  const sickHours = pick<number>(stubRecord, "sick_hours", "hours_sick");
  const sickPay = pick<number>(stubRecord, "sick_pay", "pay_sick");
  const holidayHours = pick<number>(stubRecord, "stat_holiday_hours", "holiday_hours");
  const holidayPay = pick<number>(stubRecord, "stat_holiday_pay", "holiday_pay");
  const bonus = pick<number>(stubRecord, "bonus", "bonus_pay");
  const commission = pick<number>(stubRecord, "commission", "commission_pay");
  const reimbursement = pick<number>(stubRecord, "reimbursement", "reimbursements");
  const grossPay = pick<number>(stubRecord, "gross_pay", "total_gross", "gross") || 0;

  // Deductions
  const federalTax = pick<number>(stubRecord, "federal_tax", "federal_income_tax", "tax_federal");
  const provincialTax = pick<number>(stubRecord, "provincial_tax", "state_tax", "tax_provincial", "tax_state");
  const cpp = pick<number>(stubRecord, "cpp_employee", "cpp", "social_security_employee", "social_security");
  const ei = pick<number>(stubRecord, "ei_employee", "ei", "medicare_employee", "medicare");
  const otherDed = pick<number>(stubRecord, "other_deductions", "additional_deductions");
  const totalDed = pick<number>(stubRecord, "total_deductions", "deductions", "employee_deductions") || 0;
  const netPay = pick<number>(stubRecord, "net_pay", "total_net", "net") || 0;

  // Employer contributions
  const cppEr = pick<number>(stubRecord, "cpp_employer", "social_security_employer");
  const eiEr = pick<number>(stubRecord, "ei_employer", "medicare_employer");
  const wcb = pick<number>(stubRecord, "wcb_employer", "wcb", "workers_comp");
  const employerTotal =
    pick<number>(stubRecord, "employer_contributions", "total_employer_contributions") || 0;
  const remittance = pick<number>(stubRecord, "remittance_owed", "total_remittance_owed");

  // YTD
  const ytdGross = pick<number>(stubRecord, "ytd_gross", "ytd_gross_pay");
  const ytdRegular = pick<number>(stubRecord, "ytd_regular_pay", "ytd_regular");
  const ytdOT = pick<number>(stubRecord, "ytd_overtime_pay", "ytd_overtime");
  const ytdVacation = pick<number>(stubRecord, "ytd_vacation_pay", "ytd_vacation");
  const ytdSick = pick<number>(stubRecord, "ytd_sick_pay", "ytd_sick");
  const ytdHoliday = pick<number>(stubRecord, "ytd_stat_holiday_pay", "ytd_holiday");
  const ytdBonus = pick<number>(stubRecord, "ytd_bonus");
  const ytdCommission = pick<number>(stubRecord, "ytd_commission");
  const ytdFederal = pick<number>(stubRecord, "ytd_federal_tax");
  const ytdProvincial = pick<number>(stubRecord, "ytd_provincial_tax", "ytd_state_tax");
  const ytdCpp = pick<number>(stubRecord, "ytd_cpp_employee", "ytd_cpp");
  const ytdEi = pick<number>(stubRecord, "ytd_ei_employee", "ytd_ei");
  const ytdOther = pick<number>(stubRecord, "ytd_other_deductions");
  const ytdDed = pick<number>(stubRecord, "ytd_total_deductions", "ytd_deductions");
  const ytdNet = pick<number>(stubRecord, "ytd_net_pay", "ytd_net");

  const employeeRate = pick<number>(stubRecord, "hourly_rate", "pay_rate", "rate");
  const isUS = (run?.country || stub.country) === "US";

  return (
    <div
      style={{
        background: colors.bgPage,
        minHeight: "100%",
        fontFamily: typography.fontFamily,
        padding: spacing[6] + "px " + spacing[8] + "px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ width: "100%" }}>
        <button
          onClick={() => navigate("/payroll/runs/" + runId)}
          style={{ ...backButtonStyle, marginBottom: spacing[3] }}
        >
          <ArrowLeft size={16} /> Back to pay run
        </button>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: spacing[4],
            flexWrap: "wrap",
            marginBottom: spacing[8],
          }}
        >
          <div style={{ flex: 1, minWidth: 280 }}>
            <div
              style={{
                ...typography.tiny,
                color: colors.textMuted,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 4,
              }}
            >
              Pay stub
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: spacing[3],
                marginBottom: spacing[1],
              }}
            >
              <h1
                style={{
                  ...typography.displaySm,
                  color: colors.textPrimary,
                  margin: 0,
                }}
              >
                {getStubName(stub)}
              </h1>
              <StatusPill status={status} />
            </div>
            <p
              style={{
                ...typography.body,
                color: colors.textSecondary,
                margin: 0,
              }}
            >
              {formatDate(run?.pay_period_start || stub.pay_period_start)}
              {" to "}
              {formatDate(run?.pay_period_end || stub.pay_period_end)}
              {(run?.pay_date || stub.pay_date) && (
                <>
                  {" · Pay date "}
                  {formatDate(run?.pay_date || stub.pay_date)}
                </>
              )}
            </p>
          </div>
          <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
            <Button
              variant="secondary"
              onClick={() => window.print()}
              iconLeft={<Printer size={14} />}
            >
              Print
            </Button>
          </div>
        </div>

        <Card style={{ marginBottom: spacing[5] }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: spacing[6],
            }}
          >
            <SummaryStat
              label="Gross pay"
              value={formatCurrency(grossPay, currency)}
            />
            <SummaryStat
              label="Deductions"
              value={formatCurrency(totalDed, currency)}
            />
            <SummaryStat
              label="Employer cost"
              value={formatCurrency(employerTotal, currency)}
            />
            <SummaryStat
              label="Net pay"
              value={formatCurrency(netPay, currency)}
              emphasis
            />
          </div>
        </Card>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
            gap: spacing[5],
            marginBottom: spacing[5],
          }}
        >
          <Card noPadding>
            <div
              style={{
                padding: spacing[6],
                borderBottom: "1px solid " + colors.borderSubtle,
              }}
            >
              <CardHeader
                title="Earnings"
                subtitle="Hours, rate, current pay, and year to date"
                icon={
                  <div style={iconWrapStyle}>
                    <Wallet size={18} color={colors.brandPrimary} />
                  </div>
                }
              />
            </div>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: typography.fontFamily,
                }}
              >
                <thead>
                  <tr>
                    <th style={thLeft}>Item</th>
                    <th style={thRight}>Hours</th>
                    <th style={thRight}>Rate</th>
                    <th style={thRight}>Current</th>
                    <th style={thRight}>YTD</th>
                  </tr>
                </thead>
                <tbody>
                  {(regularPay || regularHours) && (
                    <LineRow
                      label="Regular pay"
                      hours={regularHours}
                      rate={employeeRate}
                      current={regularPay}
                      ytd={ytdRegular}
                      currency={currency}
                    />
                  )}
                  {(overtimePay || overtimeHours) && (
                    <LineRow
                      label="Overtime"
                      hours={overtimeHours}
                      rate={employeeRate ? Number(employeeRate) * 1.5 : null}
                      current={overtimePay}
                      ytd={ytdOT}
                      currency={currency}
                    />
                  )}
                  {(holidayPay || holidayHours) && (
                    <LineRow
                      label="Stat holiday"
                      hours={holidayHours}
                      rate={employeeRate}
                      current={holidayPay}
                      ytd={ytdHoliday}
                      currency={currency}
                    />
                  )}
                  {(vacationPay || vacationHours) && (
                    <LineRow
                      label="Vacation"
                      hours={vacationHours}
                      rate={employeeRate}
                      current={vacationPay}
                      ytd={ytdVacation}
                      currency={currency}
                    />
                  )}
                  {(sickPay || sickHours) && (
                    <LineRow
                      label="Sick"
                      hours={sickHours}
                      rate={employeeRate}
                      current={sickPay}
                      ytd={ytdSick}
                      currency={currency}
                    />
                  )}
                  {bonus && Number(bonus) > 0 && (
                    <LineRow
                      label="Bonus"
                      current={bonus}
                      ytd={ytdBonus}
                      currency={currency}
                    />
                  )}
                  {commission && Number(commission) > 0 && (
                    <LineRow
                      label="Commission"
                      current={commission}
                      ytd={ytdCommission}
                      currency={currency}
                    />
                  )}
                  {reimbursement && Number(reimbursement) > 0 && (
                    <LineRow
                      label="Reimbursement"
                      current={reimbursement}
                      currency={currency}
                    />
                  )}
                  <LineRow
                    label="Total gross"
                    current={grossPay}
                    ytd={ytdGross}
                    bold
                    currency={currency}
                  />
                </tbody>
              </table>
            </div>
          </Card>

          <Card noPadding>
            <div
              style={{
                padding: spacing[6],
                borderBottom: "1px solid " + colors.borderSubtle,
              }}
            >
              <CardHeader
                title="Employee deductions"
                subtitle="Taxes and other amounts withheld from gross pay"
                icon={
                  <div style={iconWrapStyle}>
                    <MinusCircle size={18} color={colors.brandPrimary} />
                  </div>
                }
              />
            </div>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: typography.fontFamily,
                }}
              >
                <thead>
                  <tr>
                    <th style={thLeft}>Item</th>
                    <th style={thRight}>Current</th>
                    <th style={thRight}>YTD</th>
                  </tr>
                </thead>
                <tbody>
                  {federalTax !== null && (
                    <DeductionRow
                      label="Federal income tax"
                      current={federalTax}
                      ytd={ytdFederal}
                      currency={currency}
                    />
                  )}
                  {provincialTax !== null && (
                    <DeductionRow
                      label={isUS ? "State income tax" : "Provincial income tax"}
                      current={provincialTax}
                      ytd={ytdProvincial}
                      currency={currency}
                    />
                  )}
                  {cpp !== null && (
                    <DeductionRow
                      label={isUS ? "Social Security" : "CPP"}
                      current={cpp}
                      ytd={ytdCpp}
                      currency={currency}
                    />
                  )}
                  {ei !== null && (
                    <DeductionRow
                      label={isUS ? "Medicare" : "EI"}
                      current={ei}
                      ytd={ytdEi}
                      currency={currency}
                    />
                  )}
                  {otherDed !== null && Number(otherDed) > 0 && (
                    <DeductionRow
                      label="Other deductions"
                      current={otherDed}
                      ytd={ytdOther}
                      currency={currency}
                    />
                  )}
                  <DeductionRow
                    label="Total deductions"
                    current={totalDed}
                    ytd={ytdDed}
                    bold
                    currency={currency}
                  />
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <NetPayHighlight netPay={netPay} ytdNet={ytdNet} currency={currency} />

        {employerTotal > 0 && (
          <Card noPadding>
            <div
              style={{
                padding: spacing[6],
                borderBottom: "1px solid " + colors.borderSubtle,
              }}
            >
              <CardHeader
                title="Employer contributions"
                subtitle="What the business pays on top of gross pay. Does not reduce employee net pay."
                icon={
                  <div style={iconWrapStyle}>
                    <Building2 size={18} color={colors.brandPrimary} />
                  </div>
                }
              />
            </div>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: typography.fontFamily,
                }}
              >
                <thead>
                  <tr>
                    <th style={thLeft}>Item</th>
                    <th style={thRight}>Current</th>
                  </tr>
                </thead>
                <tbody>
                  {cppEr !== null && (
                    <DeductionRow
                      label={
                        isUS ? "Employer Social Security" : "Employer CPP"
                      }
                      current={cppEr}
                      currency={currency}
                    />
                  )}
                  {eiEr !== null && (
                    <DeductionRow
                      label={isUS ? "Employer Medicare" : "Employer EI"}
                      current={eiEr}
                      currency={currency}
                    />
                  )}
                  {wcb !== null && Number(wcb) > 0 && (
                    <DeductionRow
                      label="Workers comp / WCB"
                      current={wcb}
                      currency={currency}
                    />
                  )}
                  <DeductionRow
                    label="Total employer cost"
                    current={employerTotal}
                    bold
                    currency={currency}
                  />
                  {remittance !== null && Number(remittance) > 0 && (
                    <DeductionRow
                      label="Remittance owed"
                      current={remittance}
                      currency={currency}
                    />
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}